import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "@/components/Marquee";
import {
    MaskLine,
    MaskIn,
    Reveal,
    SectionHead,
    ParallaxImg,
    EASE,
} from "@/components/motion";
import { LogoMark } from "@/components/LogoMark";

const SERVICES = [
    { n: "01", t: "Business strategy & organisational development" },
    { n: "02", t: "Sustainability & ESG" },
    { n: "03", t: "Governance & compliance" },
    { n: "04", t: "Organisational performance" },
    { n: "05", t: "Research & advisory" },
    { n: "06", t: "Training & capacity building" },
];

const INSIGHT_TOPICS = [
    "Sustainability and ESG",
    "Corporate governance",
    "Regulation and compliance",
    "Business strategy",
];

const HERO_IMG =
    "https://images.unsplash.com/photo-1520432501437-99c0a51e3485?crop=entropy&cs=srgb&fm=jpg&q=85";

const APPROACH_IMG =
    "https://images.unsplash.com/photo-1531757721188-442621c7a53e?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function Home() {
    const heroRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: heroRef,
        offset: ["start start", "end start"],
    });
    const heroImgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
    const heroImgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
    const heroFade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

    return (
        <div>
            <section ref={heroRef} className="relative" data-testid="home-hero">
                <div className="mx-auto max-w-7xl px-6 pb-10 pt-32 md:px-12 md:pt-44">
                    <motion.div style={{ opacity: heroFade }}>
                        <Reveal y={16} delay={0.1}>
                            <p
                                className="eyebrow text-ink/50"
                                data-testid="hero-eyebrow"
                            >
                                ELCAYIL CONSULTING LTD. — London
                            </p>
                        </Reveal>

                        <h1 className="mt-8 font-serif-d text-[15vw] leading-[0.95] sm:text-[11vw] lg:text-[8.2rem]">
                            <MaskLine delay={0.2}>Excellent</MaskLine>
                            <MaskLine delay={0.34}>
                                in{" "}
                                <em className="italic text-brand">
                                    Performance
                                </em>
                                .
                            </MaskLine>
                        </h1>
                    </motion.div>
                </div>

                <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-6 pb-20 md:px-12 lg:grid-cols-12 lg:items-end">
                    <motion.div
                        style={{ y: heroImgY, scale: heroImgScale }}
                        initial={{ clipPath: "inset(0 0 100% 0)" }}
                        animate={{ clipPath: "inset(0 0 0% 0)" }}
                        transition={{ duration: 1.3, delay: 0.5, ease: EASE }}
                        className="lg:col-span-7"
                    >
                        <div className="aspect-[16/10] w-full overflow-hidden">
                            <img
                                src={HERO_IMG}
                                alt="Modern London corporate architecture"
                                className="h-full w-full object-cover"
                            />
                        </div>
                        <p className="eyebrow mt-3 text-ink/40">
                            Strategy — Sustainability — ESG — Governance
                        </p>
                    </motion.div>

                    <div className="lg:col-span-4 lg:col-start-9">
                        <MaskIn delay={0.2}>
                            <p className="font-serif-d text-2xl leading-snug md:text-[1.7rem]">
                                We work with organisations seeking more than
                                advice.
                            </p>
                        </MaskIn>
                        <Reveal delay={0.35}>
                            <p className="mt-6 text-[15px] leading-relaxed text-ink/60">
                                ELCAYIL CONSULTING LTD. provides strategic
                                advisory, sustainability, governance,
                                compliance and organisational performance
                                solutions designed to help businesses navigate
                                complexity, strengthen decision-making and
                                achieve measurable results.
                            </p>
                        </Reveal>
                        <Reveal
                            delay={0.45}
                            className="mt-8 flex flex-wrap items-center gap-4"
                        >
                            <Link
                                to="/services"
                                data-testid="hero-cta-services"
                                className="rounded-full bg-ink px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-brand"
                            >
                                Our Services
                            </Link>
                            <Link
                                to="/contact"
                                data-testid="hero-cta-contact"
                                className="link-line text-sm font-semibold text-ink"
                            >
                                Speak With Us →
                            </Link>
                        </Reveal>
                    </div>
                </div>
            </section>

            <Marquee />

            <section
                className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32"
                data-testid="home-positioning"
            >
                <SectionHead index="01" label="Positioning" />
                <div className="mt-12 grid grid-cols-1 gap-10 lg:grid-cols-12">
                    <MaskIn className="lg:col-span-8">
                        <p className="font-serif-d text-3xl leading-[1.25] md:text-5xl">
                            We help transform strategy into practical actions
                            that create long-term value.
                        </p>
                    </MaskIn>
                    <Reveal
                        delay={0.2}
                        className="lg:col-span-3 lg:col-start-10"
                    >
                        <p className="text-[15px] leading-relaxed text-ink/60">
                            Business challenges rarely exist in isolation.
                            Regulation affects strategy. Strategy affects
                            operations. Governance affects accountability.
                            Sustainability affects reputation and investment.
                        </p>
                        <Link
                            to="/approach"
                            data-testid="positioning-link-approach"
                            className="link-line mt-6 inline-block text-sm font-semibold"
                        >
                            Our Approach →
                        </Link>
                    </Reveal>
                </div>
            </section>

            <section className="border-t border-black/10" data-testid="home-services">
                <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
                    <SectionHead index="02" label="What we do" />
                    <div className="mt-12 flex flex-col justify-between gap-6 md:flex-row md:items-end">
                        <MaskIn>
                            <h2 className="font-serif-d text-4xl md:text-6xl">
                                Six practice areas,
                                <br />
                                <em className="italic text-ink/50">
                                    one standard.
                                </em>
                            </h2>
                        </MaskIn>
                        <Reveal delay={0.15}>
                            <Link
                                to="/services"
                                data-testid="services-preview-all-link"
                                className="link-line text-sm font-semibold"
                            >
                                All services →
                            </Link>
                        </Reveal>
                    </div>

                    <div className="mt-14">
                        {SERVICES.map((s, i) => (
                            <Reveal key={s.n} delay={i * 0.05} y={20}>
                                <Link
                                    to="/services"
                                    data-testid={`home-service-row-${s.n}`}
                                    className="group flex items-center justify-between border-t border-black/10 py-6 md:py-8"
                                >
                                    <div className="flex items-baseline gap-6 md:gap-10">
                                        <span className="font-mono2 text-xs text-brand">
                                            {s.n}
                                        </span>
                                        <span className="row-bg font-serif-d text-xl transition-colors duration-300 group-hover:text-brand md:text-3xl">
                                            {s.t}
                                        </span>
                                    </div>
                                    <span className="arrow-shift text-xl text-ink/40 group-hover:text-brand">
                                        →
                                    </span>
                                </Link>
                            </Reveal>
                        ))}
                        <div className="border-t border-black/10" />
                    </div>
                </div>
            </section>

            <section className="border-t border-black/10" data-testid="home-approach">
                <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 py-24 md:px-12 md:py-32 lg:grid-cols-12 lg:items-center">
                    <div className="lg:col-span-5">
                        <SectionHead index="03" label="Our approach" />
                        <MaskIn className="mt-10">
                            <h2 className="font-serif-d text-4xl leading-tight md:text-5xl">
                                From insight to action — and from action to
                                performance.
                            </h2>
                        </MaskIn>
                        <Reveal delay={0.15}>
                            <p className="mt-6 text-[15px] leading-relaxed text-ink/60">
                                Every organisation is different. Rather than
                                imposing generic solutions, we begin by
                                understanding the organisation — its operating
                                environment, challenges, objectives,
                                stakeholders and constraints.
                            </p>
                        </Reveal>
                        <Reveal delay={0.25} className="mt-8">
                            <Link
                                to="/approach"
                                data-testid="approach-teaser-link"
                                className="rounded-full border border-ink/20 px-7 py-3.5 text-sm font-semibold transition-colors duration-300 hover:bg-ink hover:text-white"
                            >
                                How we work
                            </Link>
                        </Reveal>
                    </div>
                    <Reveal
                        delay={0.2}
                        className="group lg:col-span-6 lg:col-start-7"
                    >
                        <ParallaxImg
                            src={APPROACH_IMG}
                            alt="Contemporary London architecture"
                            className="aspect-[4/3] w-full"
                        />
                    </Reveal>
                </div>
            </section>

            <section className="border-t border-black/10" data-testid="home-insights">
                <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
                    <SectionHead index="04" label="Insights" />
                    <div className="mt-12 flex flex-col justify-between gap-6 md:flex-row md:items-end">
                        <MaskIn>
                            <h2 className="font-serif-d text-4xl md:text-6xl">
                                Practical perspectives for
                                <br />
                                <em className="italic text-ink/50">
                                    decision-makers.
                                </em>
                            </h2>
                        </MaskIn>
                        <Reveal delay={0.15}>
                            <Link
                                to="/insights"
                                data-testid="insights-preview-all-link"
                                className="link-line text-sm font-semibold"
                            >
                                Read our latest insights →
                            </Link>
                        </Reveal>
                    </div>
                    <div className="mt-14">
                        {INSIGHT_TOPICS.map((t, i) => (
                            <Reveal key={t} delay={i * 0.05} y={20}>
                                <Link
                                    to="/insights"
                                    data-testid={`home-insight-row-${i + 1}`}
                                    className="group flex items-center justify-between border-t border-black/10 py-6 md:py-7"
                                >
                                    <span className="row-bg font-serif-d text-xl transition-colors duration-300 group-hover:text-brand md:text-2xl">
                                        {t}
                                    </span>
                                    <span className="arrow-shift text-lg text-ink/40 group-hover:text-brand">
                                        →
                                    </span>
                                </Link>
                            </Reveal>
                        ))}
                        <div className="border-t border-black/10" />
                    </div>
                </div>
            </section>

            <section className="bg-brand text-white" data-testid="home-cta-band">
                <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
                    <Reveal>
                        <LogoMark variant="white" className="h-12 w-auto" />
                    </Reveal>
                    <Reveal>
                        <p className="eyebrow text-white/50">Speak with us</p>
                    </Reveal>
                    <MaskIn className="mt-8 max-w-4xl">
                        <h2 className="font-serif-d text-4xl leading-tight md:text-6xl">
                            Every successful engagement begins with
                            understanding the challenge.
                        </h2>
                    </MaskIn>
                    <Reveal
                        delay={0.2}
                        className="mt-10 flex flex-wrap items-center gap-6"
                    >
                        <Link
                            to="/contact"
                            data-testid="cta-band-contact"
                            className="rounded-full bg-white px-8 py-4 text-sm font-semibold text-ink transition-colors duration-300 hover:bg-brandsoft"
                        >
                            Start a Conversation
                        </Link>
                        <p className="text-sm text-white/60">
                            Speak with ELCAYIL CONSULTING LTD. about your
                            organisation, objectives or advisory requirements.
                        </p>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
